// Global Instructions Rule Applied!
//
// How a fund status looks. One place, because the rule is easy to get wrong in
// a way nobody notices until it is on a screenshot.
//
// COLOUR COMES FROM THE FLAGS, NEVER FROM THE NAME. The catalogue is
// admin-editable — 'Closed' can be renamed, and a firm may add its own rows —
// so any `name === 'Cancelled'` match is a bug waiting to happen.
//
//   is_open      → green   (the clock is running)
//   is_terminal  → default (gone)
//   neither      → blue    (a real state, just not open — e.g. Closed,
//                           Pre-Investment, Post-Investment Period)
//
// CANCELLED IS `default`, NEVER `red`. A raise that did not come together is a
// business outcome, not a system failure, and the funds this feature exists for
// are exactly the cancelled ones. Red would paint the Funds grid with alarms
// about decisions the firm made deliberately.
//
// `tagDisplay` on a FieldConfig cannot be used here: it hardcodes color='blue'.
// These render through `displayValue` instead. Do NOT widen `tagDisplay` into a
// colour map — every grid column would then be able to mint Tag colours outside
// the house vocabulary.

import React from 'react'
import { Tag } from 'antd'
import type { FundStatusModel } from '../../state_manager/data_models/fund_status_model.ts'

export interface FundStatusFlags {
  is_open?: boolean | null
  is_terminal?: boolean | null
}

export function fundStatusTagColor(flags: FundStatusFlags | null | undefined): string {
  if (!flags) return 'default'
  if (flags.is_open) return 'green'
  if (flags.is_terminal) return 'default'
  return 'blue'
}

/** The Tag for a resolved status, or `undefined` when the fund is unclassified
 *  — NULL is "not yet classified", and a Tag reading "-" is worse than none. */
export function fundStatusTag(
  name: string | null | undefined,
  flags: FundStatusFlags | null | undefined
): React.ReactNode | undefined {
  if (!name) return undefined
  return <Tag color={fundStatusTagColor(flags)}>{name}</Tag>
}

/** Catalogue rows for one clock, ordered the way the admin panel orders them. */
export function fundStatusOptions(
  statuses: FundStatusModel[] | undefined | null,
  category: 'fundraising' | 'investment'
): { value: string; label: string }[] {
  return (statuses ?? [])
    .filter((s) => s && s.category === category)
    .slice()
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.name.localeCompare(b.name))
    .map((s) => ({ value: s.id, label: s.name }))
}
